import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AdminService, Plan, UserDetail } from '../../../core/admin/admin';
import { SiteRole } from '../../../core/auth/auth';
import { passwordValidators } from '../../../core/auth/password-validators';
import { PasswordField, PasswordInput } from '../../../shared/password-field/password-field';

/**
 * View/edit/delete a user, or — when `userId` is omitted — create a new
 * one (F-018). One component covers both since the fields mostly overlap
 * (create adds a password field; edit adds delete + send-password-reset,
 * neither of which make sense before the account exists). Mounted/
 * unmounted by the parent list page via @if, same idiom as PersonModal.
 */
@Component({
  selector: 'app-user-modal',
  imports: [DatePipe, ReactiveFormsModule, PasswordField, PasswordInput],
  templateUrl: './user-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './user-modal.css',
})
export class UserModal implements OnInit {
  private readonly admin = inject(AdminService);

  readonly userId = input<number | null>(null);
  readonly closed = output<void>();
  /** Emitted after a successful create/save/delete, so the parent list refreshes. */
  readonly changed = output<void>();

  readonly isCreate = computed(() => this.userId() === null);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly user = signal<UserDetail | null>(null);

  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);

  readonly sendingReset = signal(false);
  readonly resetSent = signal(false);
  readonly resetError = signal<string | null>(null);

  readonly confirmingDelete = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  readonly form = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    displayName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    password: new FormControl('', { nonNullable: true }),
    siteRole: new FormControl<SiteRole>('USER', { nonNullable: true }),
    plan: new FormControl<Plan>('FREE', { nonNullable: true }),
  });

  async ngOnInit(): Promise<void> {
    const id = this.userId();
    if (id === null) {
      // Create mode — the password field only exists in this mode, so its
      // validators are only attached here (otherwise form.invalid would
      // permanently block Save in edit mode, where the control still
      // exists but is never rendered).
      this.form.controls.password.setValidators(passwordValidators);
      this.form.controls.password.updateValueAndValidity();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const user = await this.admin.getUser(id);
      this.user.set(user);
      this.form.patchValue({
        email: user.email,
        displayName: user.displayName,
        siteRole: user.siteRole,
        plan: user.plan,
      });
    } catch {
      this.errorMessage.set('Could not load this user — try again.');
    } finally {
      this.loading.set(false);
    }
  }

  close(): void {
    this.closed.emit();
  }

  async save(): Promise<void> {
    if (this.isCreate()) {
      await this.create();
    } else {
      await this.update();
    }
  }

  private async create(): Promise<void> {
    this.saving.set(true);
    this.saveError.set(null);
    const raw = this.form.getRawValue();
    try {
      await this.admin.createUser({
        email: raw.email.trim(),
        displayName: raw.displayName.trim(),
        password: raw.password,
        siteRole: raw.siteRole,
        plan: raw.plan,
      });
      this.changed.emit();
      this.closed.emit();
    } catch (err) {
      this.saveError.set(this.errorMessageFor(err));
    } finally {
      this.saving.set(false);
    }
  }

  private async update(): Promise<void> {
    const current = this.user();
    if (!current) return;

    this.saving.set(true);
    this.saveError.set(null);
    const raw = this.form.getRawValue();
    try {
      const updated = await this.admin.updateUser(current.id, {
        email: raw.email.trim(),
        displayName: raw.displayName.trim(),
        siteRole: raw.siteRole,
        plan: raw.plan,
      });
      this.user.set(updated);
      this.changed.emit();
      this.closed.emit();
    } catch (err) {
      this.saveError.set(this.errorMessageFor(err));
    } finally {
      this.saving.set(false);
    }
  }

  async sendPasswordReset(): Promise<void> {
    const current = this.user();
    if (!current) return;

    this.sendingReset.set(true);
    this.resetError.set(null);
    this.resetSent.set(false);
    try {
      await this.admin.sendPasswordReset(current.id);
      this.resetSent.set(true);
      setTimeout(() => this.resetSent.set(false), 3000);
    } catch (err) {
      this.resetError.set(this.errorMessageFor(err));
    } finally {
      this.sendingReset.set(false);
    }
  }

  async confirmDelete(): Promise<void> {
    const current = this.user();
    if (!current) return;

    this.deleting.set(true);
    this.deleteError.set(null);
    try {
      await this.admin.deleteUser(current.id);
      this.changed.emit();
      this.closed.emit();
    } catch (err) {
      this.deleteError.set(this.errorMessageFor(err));
      this.confirmingDelete.set(false);
    } finally {
      this.deleting.set(false);
    }
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const message = (err.error as { message?: string } | null)?.message;
      if (message) return message;
      if (err.status === 404) {
        return 'That user no longer exists.';
      }
    }
    return 'Something went wrong — try again in a moment.';
  }
}
